import {
  Table,
  Button,
  Card,
  Row,
  Col,
  Tag,
  Input,
  Tooltip,
  Select,
} from "antd"; // Added Select
import { useEffect, useState, useMemo } from "react";
import { Api_Url } from "../../../config/config";
import axios from "axios";
import { FileMovementBulkUploadModal } from "./FileMovementBulkUploadModal";
import { ErrorToast } from "../../ToastComponent";
import moment from "moment";

const { Search } = Input;
const { Option } = Select; // Destructure Option

const formatDate = (date) => {
  if (!date) return "N/A";
  return moment(date).format("DD-MM-YYYY HH:mm");
};

export const FileMovement = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStorage, setSelectedStorage] = useState("All"); // New state for filter

  const getAuthHeader = () => ({
    headers: {
      Authorization: `Bearer ${JSON.parse(sessionStorage.getItem("user"))?.accessToken}`,
    },
  });

  // 1. Logic to get unique storage names for the dropdown
  const storageOptions = useMemo(() => {
    const uniqueStorages = [
      ...new Set(data.map((item) => item.storageName).filter(Boolean)),
    ];
    return ["All", ...uniqueStorages];
  }, [data]);

  // 2. Client-side filtering logic
  const filteredData = useMemo(() => {
    if (selectedStorage === "All") return data;
    return data.filter((item) => item.storageName === selectedStorage);
  }, [data, selectedStorage]);

  const handleExportFinalReport = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${Api_Url}api/v1/FileMovement/export-final-report`,
        {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${JSON.parse(sessionStorage.getItem("user"))?.accessToken}`,
          },
        },
      );

      const fileURL = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = fileURL;
      link.setAttribute(
        "download",
        `Final_Consolidated_Report_${moment().format("YYYYMMDD_HHmm")}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(fileURL);
    } catch (error) {
      ErrorToast("Failed to export consolidated report");
    } finally {
      setLoading(false);
    }
  };

  const fetchMovementData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${Api_Url}api/v1/FileMovement?PageNumber=1&PageSize=1000&SearchTerm=${searchTerm}`,
        getAuthHeader(),
      );
      setData(response?.data?.data || []);
    } catch (error) {
      ErrorToast("Failed to load movement logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMovementData();
  }, [searchTerm]);

  const columns = [
    {
      title: "Application No",
      dataIndex: "applicationNo",
      key: "applicationNo",
    },
    {
      title: "Loan Number",
      dataIndex: "loanNumber",
      key: "loanNumber",
      fixed: "left",
      width: 250,
    },
    {
      title: "Action Type",
      dataIndex: "actionType",
      key: "actionType",
      render: (type) => {
        let color = "default";
        if (type === "RECIEVE_FROM_BRANCH") color = "cyan";
        if (type === "SEND_TO_STORAGE") color = "purple";
        if (type === "RECEIVE_FROM_STORAGE") color = "orange";
        return <Tag color={color}>{type?.replace(/_/g, " ")}</Tag>;
      },
    },
    {
      title: "Movement Date",
      dataIndex: "movementDate",
      render: (date) => moment(date).format("DD-MM-YYYY"),
    },
    {
      title: "Storage Name",
      dataIndex: "storageName",
      render: (text) => text || "N/A",
    },
    {
      title: "File Barcode",
      dataIndex: "fileBarCode",
    },
    {
      title: "Box Barcode",
      dataIndex: "boxBarCode",
    },
    {
      title: "Submitted By",
      dataIndex: "centralOPSName",
      render: (name, record) => (
        <Tooltip title={`Code: ${record.centralOPSCode}`}>
          {`${name} (${record.centralOPSCode})`}
        </Tooltip>
      ),
    },
    {
      title: "Date",
      dataIndex: "created",
      render: (date) => moment(date).format("DD-MM-YYYY"),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="row">
          <div className="col-12 d-flex align-items-center justify-content-between">
            <h3 className="mb-0">Central Ops File Movement</h3>
            <div className="d-flex align-items-center gap-2">
              <Button
                onClick={handleExportFinalReport}
                className="btn btn-success d-flex align-items-center"
                style={{
                  backgroundColor: "#198754",
                  borderColor: "#198754",
                  color: "#fff",
                }}
                loading={loading}
              >
                <i className="fa-solid fa-file-excel me-2"></i> Export
                Consolidated Report
              </Button>
              <Button
                type="primary"
                icon={<i className="fa-solid fa-upload me-2"></i>}
                onClick={() => setIsModalOpen(true)}
              >
                Bulk Upload Movement
              </Button>
            </div>
          </div>
        </div>

        <Card className="my-3">
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={16}>
              <Search
                placeholder="Search by Loan No / Application No / Box Barcode"
                onSearch={(value) => setSearchTerm(value)}
                enterButton="Search"
                allowClear
                size="large" 
              />
            </Col>
            <Col xs={24} md={8}>
              <Select
                placeholder="Filter by Storage Name"
                style={{ width: "100%" }}
                size="large"
                value={selectedStorage}
                onChange={(value) => setSelectedStorage(value)}
              >
                {storageOptions.map((storage) => (
                  <Option key={storage} value={storage}>
                    {storage}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>
        </Card>

        <div className="table-responsive">
          <Table
            columns={columns}
            dataSource={filteredData} // Use filtered data
            rowKey="id"
            loading={loading}
            pagination={{ showSizeChanger: true, defaultPageSize: 10 }}
            scroll={{ x: 1200 }}
          />
        </div>

        {isModalOpen && (
          <FileMovementBulkUploadModal
            onClose={() => setIsModalOpen(false)}
            onSuccess={fetchMovementData}
          />
        )}
      </div>
    </div>
  );
};
